import React, { useState } from 'react';
import { Download, Share2, Mail, Link, FileText, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ExportShare = ({ onSubmit, initialData = {} }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    exportFormats: initialData.exportFormats || {
      pdf: true,
      word: false,
      excel: false,
      html: false
    },
    shareOptions: initialData.shareOptions || {
      email: false,
      link: false,
      download: true
    },
    emailRecipients: initialData.emailRecipients || '',
    emailSubject: initialData.emailSubject || 'Sustainability Report - Draft',
    emailMessage: initialData.emailMessage || '',
    linkExpiry: initialData.linkExpiry || '7',
    passwordProtection: initialData.passwordProtection || false,
    password: initialData.password || ''
  });

  const [exportStatus, setExportStatus] = useState('idle');
  const [shareStatus, setShareStatus] = useState('idle');

  const exportFormats = [
    { key: 'pdf', label: t('workflow.draftReport.exportShare.exportFormats.pdf.label'), description: t('workflow.draftReport.exportShare.exportFormats.pdf.description'), icon: FileText },
    { key: 'word', label: t('workflow.draftReport.exportShare.exportFormats.word.label'), description: t('workflow.draftReport.exportShare.exportFormats.word.description'), icon: FileText },
    { key: 'excel', label: t('workflow.draftReport.exportShare.exportFormats.excel.label'), description: t('workflow.draftReport.exportShare.exportFormats.excel.description'), icon: FileText },
    { key: 'html', label: t('workflow.draftReport.exportShare.exportFormats.html.label'), description: t('workflow.draftReport.exportShare.exportFormats.html.description'), icon: FileText }
  ];

  const shareOptions = [
    { key: 'email', label: t('workflow.draftReport.exportShare.shareOptions.email.label'), description: t('workflow.draftReport.exportShare.shareOptions.email.description'), icon: Mail },
    { key: 'link', label: t('workflow.draftReport.exportShare.shareOptions.link.label'), description: t('workflow.draftReport.exportShare.shareOptions.link.description'), icon: Link },
    { key: 'download', label: t('workflow.draftReport.exportShare.shareOptions.download.label'), description: t('workflow.draftReport.exportShare.shareOptions.download.description'), icon: Download }
  ];

  const linkExpiryOptions = [
    { value: '1', label: t('workflow.draftReport.exportShare.linkExpiryOptions.1') },
    { value: '7', label: t('workflow.draftReport.exportShare.linkExpiryOptions.7') },
    { value: '30', label: t('workflow.draftReport.exportShare.linkExpiryOptions.30') },
    { value: '90', label: t('workflow.draftReport.exportShare.linkExpiryOptions.90') },
    { value: 'never', label: t('workflow.draftReport.exportShare.linkExpiryOptions.never') }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    // Save data to localStorage
    localStorage.setItem('draftReport_exportShare', JSON.stringify(formData));
    // Navigate to Home
    navigate('/');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFormatToggle = (formatKey) => {
    setFormData(prev => ({
      ...prev,
      exportFormats: {
        ...prev.exportFormats,
        [formatKey]: !prev.exportFormats[formatKey]
      }
    }));
  };

  const handleShareToggle = (shareKey) => {
    setFormData(prev => ({
      ...prev,
      shareOptions: {
        ...prev.shareOptions,
        [shareKey]: !prev.shareOptions[shareKey]
      }
    }));
  };

  const exportReport = () => {
    setExportStatus('exporting');
    
    // Simulate export process
    setTimeout(() => {
      setExportStatus('completed');
    }, 2000);
  };

  const shareReport = () => {
    setShareStatus('sharing');
    
    // Simulate sharing process
    setTimeout(() => {
      setShareStatus('completed');
    }, 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'exporting':
      case 'sharing':
        return <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />;
      default:
        return null;
    }
  };

  const selectedFormats = Object.values(formData.exportFormats).filter(Boolean).length;
  const selectedShares = Object.values(formData.shareOptions).filter(Boolean).length;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center mb-6">
          <Download className="w-8 h-8 text-blue-500 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{t('workflow.draftReport.exportShare.title')}</h2>
            <p className="text-gray-600">{t('workflow.draftReport.exportShare.description')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Export Formats */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.draftReport.exportShare.exportFormats')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {exportFormats.map(format => {
                const IconComponent = format.icon;
                return (
                  <div
                    key={format.key}
                    className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                      formData.exportFormats[format.key]
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => handleFormatToggle(format.key)}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        {formData.exportFormats[format.key] ? (
                          <CheckCircle className="w-5 h-5 text-blue-500" />
                        ) : (
                          <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                        )}
                      </div>
                      <div className="ml-3">
                        <div className="flex items-center">
                          <IconComponent className="w-4 h-4 text-gray-500 mr-2" />
                          <h4 className="font-medium text-gray-800">{format.label}</h4>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{format.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Share Options */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.draftReport.exportShare.shareOptions')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {shareOptions.map(option => {
                const IconComponent = option.icon;
                return (
                  <div
                    key={option.key}
                    className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                      formData.shareOptions[option.key]
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => handleShareToggle(option.key)}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        {formData.shareOptions[option.key] ? (
                          <CheckCircle className="w-5 h-5 text-blue-500" />
                        ) : (
                          <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                        )}
                      </div>
                      <div className="ml-3">
                        <div className="flex items-center">
                          <IconComponent className="w-4 h-4 text-gray-500 mr-2" />
                          <h4 className="font-medium text-gray-800">{option.label}</h4>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Email Configuration */}
          {formData.shareOptions.email && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Email Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('workflow.draftReport.exportShare.emailRecipients')}
                  </label>
                  <input
                    type="text"
                    value={formData.emailRecipients}
                    onChange={(e) => handleInputChange('emailRecipients', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('workflow.draftReport.exportShare.emailRecipientsPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('workflow.draftReport.exportShare.emailSubject')}
                  </label>
                  <input
                    type="text"
                    value={formData.emailSubject}
                    onChange={(e) => handleInputChange('emailSubject', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('workflow.draftReport.exportShare.emailSubjectPlaceholder')}
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('workflow.draftReport.exportShare.emailMessage')}
                </label>
                <textarea
                  value={formData.emailMessage}
                  onChange={(e) => handleInputChange('emailMessage', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.draftReport.exportShare.emailMessagePlaceholder')}
                />
              </div>
            </div>
          )}

          {/* Link Configuration */}
          {formData.shareOptions.link && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Link Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('workflow.draftReport.exportShare.linkExpiry')}
                  </label>
                  <select
                    value={formData.linkExpiry}
                    onChange={(e) => handleInputChange('linkExpiry', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {linkExpiryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.passwordProtection}
                      onChange={(e) => handleInputChange('passwordProtection', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{t('workflow.draftReport.exportShare.passwordProtection')}</span>
                  </label>
                  {formData.passwordProtection && (
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t('workflow.draftReport.exportShare.passwordPlaceholder')}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center space-x-4 pt-6">
            <button
              type="button"
              onClick={exportReport}
              disabled={exportStatus === 'exporting'}
              className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50"
            >
              <Download className="w-4 h-4 inline mr-2" />
              {exportStatus === 'exporting' ? t('workflow.draftReport.exportShare.exporting') : t('workflow.draftReport.exportShare.exportReport')}
            </button>
            
            <button
              type="button"
              onClick={shareReport}
              disabled={shareStatus === 'sharing'}
              className="px-6 py-3 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors duration-200 disabled:opacity-50"
            >
              <Share2 className="w-4 h-4 inline mr-2" />
              {shareStatus === 'sharing' ? t('workflow.draftReport.exportShare.sharing') : t('workflow.draftReport.exportShare.shareReport')}
            </button>
          </div>

          {/* Status Messages */}
          {exportStatus === 'completed' && (
            <div className="text-center text-green-600 bg-green-50 p-4 rounded-lg">
              {t('workflow.draftReport.exportShare.exportCompleted')}
            </div>
          )}
          
          {shareStatus === 'completed' && (
            <div className="text-center text-green-600 bg-green-50 p-4 rounded-lg">
              {t('workflow.draftReport.exportShare.shareCompleted')}
            </div>
          )}

        </form>
      </div>
    </div>
  );
};

export default ExportShare;
